import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsTouchUi } from '@/shared/lib/media';

import styles from './ChoiceList.module.css';

export interface ChoiceOption {
  value: string;
  /** Plain text: the accessible name, the search text. */
  label: string;
  /** How the option looks in the list (a dot, a badge, a person pill); defaults to the label. */
  node?: ReactNode;
}

interface Props {
  /** Accessible name of the list. */
  label: string;
  options: readonly ChoiceOption[];
  value: readonly string[];
  /** Several values can be ticked; the list stays open. Otherwise a pick ends the choice. */
  multiple?: boolean;
  /** Single choice only: the text of the row that clears the value (omit when it may not be empty). */
  emptyLabel?: string;
  onChange: (value: string[]) => void;
  /** A single pick was made / "Done" was pressed (multiple). */
  onDone?: () => void;
}

/** Lists longer than this get a search field. */
const SEARCH_FROM = 7;

/**
 * Planosfera's choice list for database fields (a replacement of the browser `<select>`): an
 * optional search over a listbox of options drawn as the caller wants, with the current value
 * marked. Arrow keys / Home / End move, Enter (and Space on the list) picks, Escape is left to the
 * surrounding popover / panel. Focus stays on the search field (or the list), the active option is
 * announced through `aria-activedescendant`.
 */
export function ChoiceList({
  label,
  options,
  value,
  multiple,
  emptyLabel,
  onChange,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const id = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const field = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const searchable = options.length > SEARCH_FROM;
  const needle = query.trim().toLocaleLowerCase();
  const shown = options.filter(
    (o) => needle === '' || o.label.toLocaleLowerCase().includes(needle),
  );
  const clearRow = !multiple && emptyLabel !== undefined && needle === '';
  const rows: { value: string | null; label: string; node: ReactNode }[] = [
    ...(clearRow ? [{ value: null, label: emptyLabel, node: emptyLabel }] : []),
    ...shown.map((o) => ({ value: o.value, label: o.label, node: o.node ?? o.label })),
  ];
  const current = Math.min(active, Math.max(0, rows.length - 1));
  const selected = (row: string | null) =>
    row === null ? value.length === 0 : value.includes(row);

  // Touch: the list takes the focus, never the search field (it would raise the on-screen keyboard
  // unasked); a tap on the field focuses it deliberately.
  const touch = useIsTouchUi();
  useEffect(() => {
    (searchable && !touch ? field.current : list.current)?.focus({ preventScroll: true });
  }, [searchable, touch]);
  useEffect(() => {
    const row = document.getElementById(`${id}-${String(current)}`);
    // (jsdom has no scrollIntoView)
    if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [id, current]);

  const pick = (row: string | null) => {
    if (row === null) {
      onChange([]);
      onDone?.();
    } else if (multiple) {
      onChange(value.includes(row) ? value.filter((v) => v !== row) : [...value, row]);
    } else {
      onChange([row]);
      onDone?.();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (rows.length > 0 && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      setActive(
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? rows.length - 1
            : (current + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length,
      );
    } else if (event.key === 'Enter' || (event.key === ' ' && event.target === list.current)) {
      const row = rows[current];
      event.preventDefault();
      if (row) pick(row.value);
      else if (multiple) onDone?.();
    }
  };

  const activeId = rows.length > 0 ? `${id}-${String(current)}` : undefined;
  return (
    <div className={styles.root} onKeyDown={onKeyDown}>
      {searchable && (
        <input
          ref={field}
          type="search"
          className={styles.search}
          role="combobox"
          aria-expanded
          aria-controls={`${id}-list`}
          aria-activedescendant={activeId}
          aria-label={t('common.search')}
          placeholder={t('common.search')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
        />
      )}
      <div
        ref={list}
        id={`${id}-list`}
        className={styles.list}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        tabIndex={searchable ? -1 : 0}
        aria-activedescendant={activeId}
      >
        {rows.map((row, index) => {
          const on = selected(row.value);
          return (
            <div
              key={row.value ?? 'none'}
              id={`${id}-${String(index)}`}
              role="option"
              aria-selected={on}
              aria-label={row.label}
              data-active={index === current ? 'true' : undefined}
              className={styles.option}
              // keep the focus where it is: the caret stays in the search field
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => {
                setActive(index);
                pick(row.value);
              }}
            >
              <span aria-hidden className={multiple ? styles.box : styles.tick} data-on={on}>
                {on ? '✓' : ''}
              </span>
              <span className={styles.optionBody}>{row.node}</span>
            </div>
          );
        })}
        {rows.length === 0 && <p className={styles.empty}>{t('common.noResults')}</p>}
      </div>
      {multiple && onDone && (
        <button type="button" className={styles.done} onClick={onDone}>
          {t('common.done')}
        </button>
      )}
    </div>
  );
}
